import { useCallback, useEffect, useRef, useState } from "react";
import type { EvidenceDto } from "@audit-plan/shared";
import { EVIDENCE_MAX_BYTES } from "@audit-plan/shared";
import { UploadError, checkFile, fileExt, isAbort, prepareImage, uploadEvidence, type UploadCtx } from "../../api/evidence";

export type QStatus = "ready" | "preparing" | "uploading" | "done" | "error";

export interface QItem {
  id: string;
  file: File;
  /** File dopo la compressione (calcolato una volta sola, riusato dai nuovi tentativi). */
  prepared?: File;
  preview?: string;
  status: QStatus;
  progress: number;
  /** Chiave i18n o messaggio grezzo. */
  error?: string;
  retrying?: number;
  evidence?: EvidenceDto;
}

let seq = 0;
const isImg = (f: File) => /^image\//.test(f.type) || ["jpg", "jpeg", "png", "heic", "heif"].includes(fileExt(f.name));

/**
 * Coda di caricamento: aggiunge file (validati), li comprime, li carica uno dopo l'altro con
 * avanzamento, tentativi automatici e ripresa manuale. Ogni file diventa un'evidenza.
 */
export function useUploadQueue(opts: { multiple?: boolean } = {}) {
  const multiple = opts.multiple ?? true;
  const [items, setItems] = useState<QItem[]>([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef<QItem[]>([]);
  const ctl = useRef<AbortController | null>(null);
  ref.current = items;

  const patch = useCallback((id: string, p: Partial<QItem>) => setItems((cur) => cur.map((x) => (x.id === id ? { ...x, ...p } : x))), []);

  useEffect(
    () => () => {
      ctl.current?.abort();
      for (const x of ref.current) if (x.preview) URL.revokeObjectURL(x.preview);
    },
    [],
  );

  const add = useCallback(
    (files: File[]) => {
      const next: QItem[] = [];
      for (const file of files) {
        let error: string | undefined;
        try {
          checkFile({ name: file.name, size: 0 } as File); // solo estensione: la dimensione si controlla dopo la compressione
        } catch (e) {
          error = e instanceof UploadError ? e.key : "error";
        }
        next.push({
          id: `q${++seq}`,
          file,
          preview: isImg(file) && !error ? URL.createObjectURL(file) : undefined,
          status: error ? "error" : "ready",
          progress: 0,
          error,
        });
      }
      setItems((cur) => {
        if (multiple) return [...cur, ...next];
        for (const x of cur) if (x.preview) URL.revokeObjectURL(x.preview);
        return next.slice(-1);
      });
    },
    [multiple],
  );

  const remove = useCallback((id: string) => {
    setItems((cur) => {
      const x = cur.find((i) => i.id === id);
      if (x?.preview) URL.revokeObjectURL(x.preview);
      return cur.filter((i) => i.id !== id);
    });
  }, []);

  const reset = useCallback(() => {
    ctl.current?.abort();
    setItems((cur) => {
      for (const x of cur) if (x.preview) URL.revokeObjectURL(x.preview);
      return [];
    });
    setBusy(false);
  }, []);

  const cancel = useCallback(() => ctl.current?.abort(), []);

  /**
   * Carica tutti i file non ancora caricati. Ritorna le evidenze create (anche da giri precedenti)
   * e se e' tutto andato a buon fine. `keepExt` = estensione vincolata (nuova versione di un'evidenza).
   */
  const upload = useCallback(
    async (ctx: UploadCtx, description = "", evidenceId?: string, keepExt?: string): Promise<{ ok: boolean; evidence: EvidenceDto[] }> => {
      const ac = new AbortController();
      ctl.current = ac;
      setBusy(true);
      let ok = true;
      for (const it of ref.current) {
        if (it.status === "done" || (it.status === "error" && it.error === "b_ext_bad")) {
          if (it.status !== "done") ok = false;
          continue;
        }
        if (ac.signal.aborted) {
          ok = false;
          break;
        }
        try {
          let f = it.prepared;
          if (!f) {
            patch(it.id, { status: "preparing", error: undefined, retrying: 0, progress: 0 });
            f = await prepareImage(it.file, keepExt);
            if (f.size > EVIDENCE_MAX_BYTES) throw new UploadError("b_file_big");
            patch(it.id, { prepared: f });
          }
          patch(it.id, { status: "uploading", error: undefined, retrying: 0, progress: 0 });
          const ev = await uploadEvidence(f, ctx, description, evidenceId, {
            signal: ac.signal,
            onProgress: (p) => patch(it.id, { progress: p }),
            onRetry: (n) => patch(it.id, { retrying: n }),
          });
          patch(it.id, { status: "done", progress: 100, evidence: ev, retrying: 0 });
        } catch (e) {
          ok = false;
          patch(it.id, { status: "error", retrying: 0, error: isAbort(e) ? "b_up_cancelled" : e instanceof UploadError ? e.key : e instanceof Error ? e.message : String(e) });
        }
      }
      setBusy(false);
      const evidence = ref.current.map((x) => x.evidence).filter((x): x is EvidenceDto => !!x);
      return { ok, evidence };
    },
    [patch],
  );

  return { items, busy, add, remove, reset, cancel, upload };
}
